import type { PageObjectVars } from '@sosocio/creator-types/app';
import type { BorderImageModel } from '@sosocio/creator-types/database';
import type {
	PageModel,
	PageObjectModel,
} from '@sosocio/creator-types/project';

export interface CalculatePageObjectPositionOptions {
	borderImage?: BorderImageModel;
	drawPage?: boolean;
	useMaximumWidthFallback?: boolean;
}

type PageObjectCorners = Pick<
	PageObjectVars,
	'bottomleft' | 'bottomright' | 'topleft' | 'topright'
>;

export type PageObjectMaximumSize = Pick<PageObjectModel, 'height' | 'width'>;

export type PageObjectPositionModel = (
	Pick<
		PageObjectModel,
		| 'bordercolor'
		| 'borderwidth'
		| 'height'
		| 'type'
		| 'width'
		| 'x_axis'
		| 'y_axis'
	>
	& Partial<Pick<
		PageObjectModel,
		'cropheight' | 'cropwidth' | 'cropx' | 'cropy' | 'maxwidth'
	>>
	& {
		angle?: PageObjectVars['angle'];
		flip?: boolean;
		flop?: boolean;
	}
);

/**
 * Copies the four object corners before flip transforms reorder their coordinates.
 *
 * @param vars Calculated {@link PageObjectVars} containing the corners to detach.
 * @returns Independent corner coordinates safe to use while mutating the result.
 */
function copyPageObjectCorners(vars: PageObjectVars): PageObjectCorners {
	return {
		bottomleft: {
			x: vars.bottomleft.x,
			y: vars.bottomleft.y,
		},
		bottomright: {
			x: vars.bottomright.x,
			y: vars.bottomright.y,
		},
		topleft: {
			x: vars.topleft.x,
			y: vars.topleft.y,
		},
		topright: {
			x: vars.topright.x,
			y: vars.topright.y,
		},
	};
}

/**
 * Calculates canonical object, image-map, detached-canvas, and draw coordinates without reading application state.
 *
 * The caller owns photo, offering, and quality lookups and supplies their resolved
 * maximum dimensions. This keeps the geometry identical for Creator, kiosk, and
 * render workers without coupling the calculation to any store implementation.
 *
 * @param referenceModel {@link PageObjectPositionModel} geometry to position.
 * @param maximumObjectSize Store-resolved maximum source dimensions.
 * @param scaling Scale applied to image-map and detached-canvas coordinates.
 * @param pageModel Owning page bounds, or `null` when rendering without a page.
 * @param bleedMargin Unscaled page bleed added around the object coordinates.
 * @param options Optional border-image bounds and page-drawing behavior.
 * @returns Canonical {@link PageObjectVars} used by canvas renderers and interactions.
 */
export function calculatePageObjectPosition(
	referenceModel: PageObjectPositionModel,
	maximumObjectSize: PageObjectMaximumSize,
	scaling: number,
	pageModel: Pick<PageModel, 'height' | 'width'> | null,
	bleedMargin: number,
	options: CalculatePageObjectPositionOptions = {},
): PageObjectVars {
	const dpiScale = (
		options.useMaximumWidthFallback
		&& !referenceModel.maxwidth
			? 1
			: referenceModel.maxwidth! / maximumObjectSize.width
	);
	const normalizedBleedMargin = bleedMargin || 0;
	const vars: PageObjectVars = {
		angle: referenceModel.angle || 0,
		bordercolor: referenceModel.bordercolor,
		borderwidth: referenceModel.borderwidth,
		bottomleft: {
			x: 0,
			y: 0,
		},
		bottomright: {
			x: 0,
			y: 0,
		},
		canvas: {
			bottom: 0,
			height: 0,
			left: 0,
			right: 0,
			top: 0,
			width: 0,
		},
		center: {
			x: 0,
			y: 0,
		},
		cosinus: 0,
		cropheight: referenceModel.cropheight || 0,
		cropwidth: referenceModel.cropwidth || 0,
		cropx: referenceModel.cropx || 0,
		cropy: referenceModel.cropy || 0,
		draw: {
			x: 0,
			y: 0,
		},
		flip: referenceModel.flip ? 1 : 0,
		flop: referenceModel.flop ? 1 : 0,
		height: referenceModel.height,
		imagemap: {
			bottomleft: {
				x: 0,
				y: 0,
			},
			bottomright: {
				x: 0,
				y: 0,
			},
			topleft: {
				x: 0,
				y: 0,
			},
			topright: {
				x: 0,
				y: 0,
			},
		},
		max: {
			cropx: 0,
			cropy: 0,
			height: 0,
			placement: {
				x: 0,
				y: 0,
			},
			topleft: {
				x: 0,
				y: 0,
			},
			width: 0,
			x_axis: 0,
			y_axis: 0,
		},
		maxheight: maximumObjectSize.height,
		maxwidth: maximumObjectSize.width,
		placement: {
			x: 0,
			y: 0,
		},
		rotate: 0,
		sinus: 0,
		topleft: {
			x: 0,
			y: 0,
		},
		topright: {
			x: 0,
			y: 0,
		},
		width: referenceModel.width,
		x_axis: referenceModel.x_axis + normalizedBleedMargin,
		y_axis: referenceModel.y_axis + normalizedBleedMargin,
	};
	vars.center = {
		x: vars.x_axis + vars.width / 2,
		y: vars.y_axis + vars.height / 2,
	};

	const objectscaleW = vars.cropwidth / vars.width;
	const objectscaleH = vars.cropheight / vars.height;

	vars.max = {
		cropx: 0,
		cropy: 0,
		height: vars.maxheight / objectscaleH * dpiScale,
		placement: {
			x: 0,
			y: 0,
		},
		topleft: {
			x: 0,
			y: 0,
		},
		width: vars.maxwidth / objectscaleW * dpiScale,
		x_axis: vars.x_axis - vars.cropx / objectscaleW,
		y_axis: vars.y_axis - vars.cropy / objectscaleH,
	};
	vars.max.center = {
		x: vars.max.x_axis + vars.max.width / 2,
		y: vars.max.y_axis + vars.max.height / 2,
	};

	if (vars.flop) {
		vars.angle = -vars.angle;
	}

	if (vars.flip) {
		vars.angle = -vars.angle;
	}

	vars.rotate = Math.PI / 180 * vars.angle;
	vars.cosinus = Math.cos(vars.rotate);
	vars.sinus = Math.sin(vars.rotate);

	vars.topleft.x = (
		(-(vars.width) / 2 * Math.cos(vars.rotate))
		- (-(vars.height) / 2 * Math.sin(vars.rotate))
	) + vars.center.x;
	vars.imagemap.topleft.x = (
		(-(vars.width + 2 * vars.borderwidth) / 2 * Math.cos(vars.rotate))
		- (-(vars.height + 2 * vars.borderwidth) / 2 * Math.sin(vars.rotate))
	) + vars.center.x;

	vars.topleft.y = (
		(-(vars.width) / 2 * Math.sin(vars.rotate))
		+ (-(vars.height) / 2 * Math.cos(vars.rotate))
	) + vars.center.y;
	vars.imagemap.topleft.y = (
		(-(vars.width + 2 * vars.borderwidth) / 2 * Math.sin(vars.rotate))
		+ (-(vars.height + 2 * vars.borderwidth) / 2 * Math.cos(vars.rotate))
	) + vars.center.y;

	vars.max.topleft.x = (
		(-(vars.max.width) / 2 * Math.cos(vars.rotate))
		- (-(vars.max.height) / 2 * Math.sin(vars.rotate))
	) + vars.max.center.x;
	vars.max.topleft.y = (
		(-(vars.max.width) / 2 * Math.sin(vars.rotate))
		+ (-(vars.max.height) / 2 * Math.cos(vars.rotate))
	) + vars.max.center.y;

	vars.topright.x = vars.topleft.x + vars.width * vars.cosinus;
	vars.imagemap.topright.x = vars.imagemap.topleft.x
		+ (vars.width + 2 * vars.borderwidth) * vars.cosinus;

	vars.topright.y = vars.topleft.y + vars.width * vars.sinus;
	vars.imagemap.topright.y = vars.imagemap.topleft.y
		+ (vars.width + 2 * vars.borderwidth) * vars.sinus;

	vars.bottomright.x = vars.topright.x - vars.height * vars.sinus;
	vars.imagemap.bottomright.x = vars.imagemap.topright.x
		- (vars.height + 2 * vars.borderwidth) * vars.sinus;

	vars.bottomright.y = vars.topright.y + vars.height * vars.cosinus;
	vars.imagemap.bottomright.y = vars.imagemap.topright.y
		+ (vars.height + 2 * vars.borderwidth) * vars.cosinus;

	vars.bottomleft.x = vars.topleft.x - vars.height * vars.sinus;
	vars.imagemap.bottomleft.x = vars.imagemap.topleft.x
		- (vars.height + 2 * vars.borderwidth) * vars.sinus;

	vars.bottomleft.y = vars.topleft.y + vars.height * vars.cosinus;
	vars.imagemap.bottomleft.y = vars.imagemap.topleft.y
		+ (vars.height + 2 * vars.borderwidth) * vars.cosinus;

	vars.placement = {
		x: vars.topleft.x,
		y: vars.topleft.y,
	};
	vars.max.placement = {
		x: vars.max.topleft.x,
		y: vars.max.topleft.y,
	};

	if (vars.flop) {
		const corners = copyPageObjectCorners(vars);

		vars.placement.x = -vars.bottomright.x;
		vars.topleft.x = corners.bottomleft.x;
		vars.topleft.y = corners.topright.y;
		vars.topright.x = corners.bottomright.x;
		vars.topright.y = corners.topleft.y;
		vars.bottomright.x = corners.topright.x;
		vars.bottomright.y = corners.bottomleft.y;
		vars.bottomleft.x = corners.topleft.x;
		vars.bottomleft.y = corners.bottomright.y;
	}

	if (vars.flip) {
		const corners = copyPageObjectCorners(vars);

		vars.placement.y = (
			vars.flop
				? -vars.bottomleft.y
				: -vars.bottomright.y
		);
		vars.topleft.x = corners.bottomleft.x;
		vars.topleft.y = corners.topright.y;
		vars.topright.x = corners.bottomright.x;
		vars.topright.y = corners.topleft.y;
		vars.bottomright.x = corners.topright.x;
		vars.bottomright.y = corners.bottomleft.y;
		vars.bottomleft.x = corners.topleft.x;
		vars.bottomleft.y = corners.bottomright.y;
	}

	vars.imagemap = {
		bottomleft: {
			x: vars.imagemap.bottomleft.x * scaling,
			y: vars.imagemap.bottomleft.y * scaling,
		},
		bottomright: {
			x: vars.imagemap.bottomright.x * scaling,
			y: vars.imagemap.bottomright.y * scaling,
		},
		topleft: {
			x: vars.imagemap.topleft.x * scaling,
			y: vars.imagemap.topleft.y * scaling,
		},
		topright: {
			x: vars.imagemap.topright.x * scaling,
			y: vars.imagemap.topright.y * scaling,
		},
	};

	const pageWidth = (
		pageModel
			? pageModel.width * scaling
			: 1000000
	);
	const pageHeight = (
		pageModel
			? pageModel.height * scaling
			: 100000
	);

	vars.canvas.top = Math.max(
		0,
		Math.min(
			vars.imagemap.topleft.y,
			vars.imagemap.topright.y,
			vars.imagemap.bottomleft.y,
			vars.imagemap.bottomright.y,
		),
	);
	vars.canvas.left = Math.max(
		0,
		Math.min(
			vars.imagemap.topleft.x,
			vars.imagemap.topright.x,
			vars.imagemap.bottomleft.x,
			vars.imagemap.bottomright.x,
		),
	);
	vars.canvas.bottom = Math.min(
		pageHeight + 2 * normalizedBleedMargin * scaling,
		Math.max(
			vars.imagemap.topleft.y,
			vars.imagemap.topright.y,
			vars.imagemap.bottomleft.y,
			vars.imagemap.bottomright.y,
		),
	);
	vars.canvas.right = Math.min(
		pageWidth + 2 * normalizedBleedMargin * scaling,
		Math.max(
			vars.imagemap.topleft.x,
			vars.imagemap.topright.x,
			vars.imagemap.bottomleft.x,
			vars.imagemap.bottomright.x,
		),
	);

	if (referenceModel.type === 'text') {
		/**
		 * Text glyphs can exceed the configured object bounds, so the detached
		 * canvas extends to the complete page instead of clipping the raster.
		 */
		vars.canvas.left = 0;
		vars.canvas.right = pageWidth + 2 * normalizedBleedMargin * scaling;
		vars.canvas.bottom = pageHeight + 2 * normalizedBleedMargin * scaling;
	} else if (options.borderImage) {
		const borderImageInnerWidth = options.borderImage.width
			- options.borderImage.borderwidth_left
			- options.borderImage.borderwidth_right;
		const borderScale = vars.width / borderImageInnerWidth;
		const borderWidthBottom = Math.round(options.borderImage.borderwidth_bottom * borderScale);
		const borderWidthLeft = Math.round(options.borderImage.borderwidth_left * borderScale);
		const borderWidthRight = Math.round(options.borderImage.borderwidth_right * borderScale);
		const borderWidthTop = Math.round(options.borderImage.borderwidth_top * borderScale);

		vars.canvas.left = Math.max(
			0,
			vars.canvas.left - borderWidthLeft,
		);
		vars.canvas.right = Math.min(
			pageWidth + 2 * normalizedBleedMargin * scaling,
			vars.canvas.right + borderWidthRight,
		);
		vars.canvas.top = Math.max(
			0,
			vars.canvas.top - borderWidthTop,
		);
		vars.canvas.bottom = Math.min(
			pageHeight + 2 * normalizedBleedMargin * scaling,
			vars.canvas.bottom + borderWidthBottom,
		);
	}

	vars.canvas.width = vars.canvas.right - vars.canvas.left + 2;
	vars.canvas.height = vars.canvas.bottom - vars.canvas.top + 2;
	vars.draw = {
		x: (
			options.drawPage
				? vars.placement.x
				: vars.placement.x - vars.canvas.left / scaling + 1
		),
		y: (
			options.drawPage
				? vars.placement.y
				: vars.placement.y - vars.canvas.top / scaling + 1
		),
	};

	return vars;
}
